<template>
  <div>
    <!-- Loading & Error Indicator -->
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#68D389"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <!-- Toast Notification / Snackbar -->
    <v-snackbar v-model="showSnackBar" color="#68D389" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: #ffffff">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showSnackBar = false">
            <v-icon style="color: #ffffff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <!-- Main Container -->
    <v-layout wrap justify-center class="poppinsregular">
      <v-flex xs12 pa-4 align-self-center text-left>
        <span class="poppinsbold" style="font-size: 20px">New Booking Excel Report</span>
      </v-flex>

      <v-flex xs12 md10 lg8 pa-4>
        <v-card class="elevation-2 rounded-lg pa-6">
          <v-card-text>
            <v-layout wrap>
              <!-- From Date Picker -->
              <v-flex xs12 sm6 md4 pa-3>
                <v-menu
                  ref="menu1"
                  v-model="menu1"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="fromDate"
                      label="From Date"
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                      clearable
                      prepend-inner-icon="mdi-calendar"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="fromDate"
                    no-title
                    @input="menu1 = false"
                  ></v-date-picker>
                </v-menu>
              </v-flex>

              <!-- To Date Picker -->
              <v-flex xs12 sm6 md4 pa-3>
                <v-menu
                  ref="menu2"
                  v-model="menu2"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="toDate"
                      label="To Date"
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                      clearable
                      prepend-inner-icon="mdi-calendar"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="toDate"
                    no-title
                    @input="menu2 = false"
                  ></v-date-picker>
                </v-menu>
              </v-flex>

              <!-- Program Selector -->
              <v-flex xs12 sm12 md4 pa-3>
                <v-select
                  v-model="programme"
                  :items="programs"
                  item-text="name"
                  item-value="value"
                  outlined
                  dense
                  label="Program"
                  prepend-inner-icon="mdi-format-list-bulleted"
                ></v-select>
              </v-flex>

              <!-- Description Notice -->
              <v-flex xs12 pa-3 class="my-4">
                <v-alert
                  outlined
                  color="#68D389"
                  type="info"
                  class="rounded-lg"
                >
                  Configure the desired date range and select a specific program (or leave as 'All Programs') to generate and download the booking report as an Excel file.
                </v-alert>
              </v-flex>

              <!-- Actions -->
              <v-flex xs12 pa-3 text-center>
                <v-btn
                  color="#68D389"
                  dark
                  large
                  class="px-8 rounded-lg poppinssemibold"
                  style="text-transform: none; letter-spacing: 0.5px;"
                  @click="downloadExcel"
                >
                  <v-icon left>mdi-file-excel</v-icon>
                  Download Excel Report
                </v-btn>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showSnackBar: false,
      timeout: 5000,
      msg: null,

      fromDate: null,
      toDate: null,
      programme: null,

      menu1: false,
      menu2: false,
      programs: [],
    };
  },

  beforeMount() {
    // Initialize date filters: Defaulting fromDate to 15 days ago and toDate to today
    const date = new Date();
    date.setDate(date.getDate() - 15);
    this.fromDate = date.toISOString().substr(0, 10);
    this.toDate = new Date().toISOString().substr(0, 10);

    this.getPrograms();
  },

  methods: {
    getPrograms() {
      this.appLoading = true;
      axios({
        url: "/programme/getlist",
        method: "GET",
        params: { programmestatus: "started" },
        headers: {
          token: localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            const temp = response.data.data.map((item) => ({
              name: item.progName,
              value: item._id,
            }));
            this.programs = temp;
            this.programs.unshift({ name: "All Programs", value: null });
            this.programme = null; // Default to 'All Programs'
          } else {
            this.showSnackBar = true;
            this.msg = response.data.msg || "Failed to load programs list";
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error("Load Programs Error: ", err);
        });
    },

    downloadExcel() {
      if (!this.fromDate || !this.toDate) {
        this.showSnackBar = true;
        this.msg = "Please select both From and To dates.";
        return;
      }

      this.appLoading = true;
      axios({
        method: "GET",
        url: "/report/booking-report-excel",
        headers: {
          token: localStorage.getItem("token"),
        },
        params: {
          fromDate: this.fromDate,
          toDate: this.toDate,
          programme: this.programme,
        },
        responseType: "blob",
      })
        .then((response) => {
          this.appLoading = false;
          
          // Check if response contains valid blob content
          if (response.data) {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            
            const filename = `BookingReport_${this.fromDate}_to_${this.toDate}.xlsx`;
            link.setAttribute("download", filename);
            
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            this.showSnackBar = true;
            this.msg = "Excel report downloaded successfully!";
          } else {
            this.showSnackBar = true;
            this.msg = "No data returned for the Excel report.";
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.showSnackBar = true;
          this.msg = "Failed to download Excel report.";
          console.error("Download Excel Error: ", err);
        });
    },
  },
};
</script>

<style scoped>
/* Scoped styles matching modern Poppins theme */
.poppinsregular {
  font-family: poppinsregular;
}
.poppinsbold {
  font-family: poppinsbold;
}
.poppinssemibold {
  font-family: poppinssemibold;
}
</style>
